import { cilSave, cilTrash } from '@coreui/icons';

import CIcon from '@coreui/icons-react';

import { CButton, CFormInput, CTableDataCell, CTableRow } from '@coreui/react';

import React, { useState } from 'react';

import { brokenSubscriptionsApi } from '../api';

import type { PurrscribeProduct } from '../types';

import { PURRSCRIBE_QUANTITY_MAX, QUANTITY_MIN } from './BrokenSubscriptionDetailPage.constants';
import { formatPrice } from './BrokenSubscriptionDetailPage.utils';

export type PurrscribeProductRowProps = {
  product: PurrscribeProduct;
  canEdit: boolean;
  validLineIds: Set<string>;
  shopifyCustomerId: number;
  actionInProgress: boolean;
  onSave: () => void;
  onError: (msg: string) => void;
  onDelete: () => void;
};

export function PurrscribeProductRow({
  product,
  canEdit,
  validLineIds,
  shopifyCustomerId,
  actionInProgress,
  onSave,
  onError,
  onDelete,
}: PurrscribeProductRowProps) {
  const cappedQty = Math.min(Number(product.quantity) || 0, PURRSCRIBE_QUANTITY_MAX);
  const [qty, setQty] = useState(String(cappedQty || product.quantity));
  const [shopifyLineId, setShopifyLineId] = useState(product.shopifyLineId ?? '');
  const [previousProduct, setPreviousProduct] = useState({
    quantity: product.quantity,
    shopifyLineId: product.shopifyLineId,
  });
  const [saving, setSaving] = useState(false);

  if (
    product.quantity !== previousProduct.quantity ||
    product.shopifyLineId !== previousProduct.shopifyLineId
  ) {
    setPreviousProduct({ quantity: product.quantity, shopifyLineId: product.shopifyLineId });
    const next = Math.min(Number(product.quantity) || 0, PURRSCRIBE_QUANTITY_MAX);
    setQty(String(next || product.quantity));
    setShopifyLineId(product.shopifyLineId ?? '');
  }

  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    const n = parseInt(v, 10);
    if (!Number.isNaN(n) && n > PURRSCRIBE_QUANTITY_MAX) setQty(String(PURRSCRIBE_QUANTITY_MAX));
    else setQty(v);
  };

  const handleSave = async () => {
    const num = parseInt(qty, 10);
    if (Number.isNaN(num) || num < QUANTITY_MIN || num > PURRSCRIBE_QUANTITY_MAX) {
      onError(`Quantity must be between ${QUANTITY_MIN} and ${PURRSCRIBE_QUANTITY_MAX}`);
      return;
    }
    const lineIdTrimmed = shopifyLineId.trim();
    if (lineIdTrimmed && !validLineIds.has(lineIdTrimmed)) {
      onError('Shopify Line ID must exist in the Shopify contract.');
      return;
    }
    const payload: { qty?: number; shopifyLineId?: string | null } = {};
    if (num !== product.quantity) payload.qty = num;
    if (lineIdTrimmed !== (product.shopifyLineId ?? ''))
      payload.shopifyLineId = lineIdTrimmed || null;
    if (Object.keys(payload).length === 0) return;
    setSaving(true);
    try {
      await brokenSubscriptionsApi.updateProduct(shopifyCustomerId, product.id, payload);
      onSave();
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String(e.message) : 'Save failed';
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CTableRow>
      <CTableDataCell>{product.productName ?? ''}</CTableDataCell>
      <CTableDataCell>{product.productVariantId ?? ''}</CTableDataCell>
      <CTableDataCell>{product.productSku ?? ''}</CTableDataCell>
      <CTableDataCell>
        {canEdit ? (
          <CFormInput
            type="text"
            value={shopifyLineId}
            onChange={(e) => setShopifyLineId(e.target.value)}
            disabled={actionInProgress}
            placeholder="Line ID"
          />
        ) : (
          (product.shopifyLineId ?? '')
        )}
      </CTableDataCell>
      <CTableDataCell>
        {canEdit ? (
          <CFormInput
            type="number"
            min={QUANTITY_MIN}
            max={PURRSCRIBE_QUANTITY_MAX}
            value={qty}
            onChange={handleQtyChange}
            disabled={actionInProgress}
            style={{ maxWidth: 80 }}
          />
        ) : (
          Math.min(Number(product.quantity) || 0, PURRSCRIBE_QUANTITY_MAX)
        )}
      </CTableDataCell>
      <CTableDataCell>{formatPrice(product.linePrice)}</CTableDataCell>
      {canEdit && (
        <CTableDataCell>
          <CButton
            color="primary"
            size="sm"
            variant="ghost"
            aria-label="Save quantity"
            title="Save quantity"
            onClick={(...args: Parameters<typeof handleSave>) => void handleSave(...args)}
            disabled={saving || actionInProgress}
            className="me-1"
          >
            <CIcon icon={cilSave} />
          </CButton>
          <CButton
            color="danger"
            size="sm"
            variant="ghost"
            aria-label="Delete line"
            title="Delete line"
            onClick={onDelete}
            disabled={actionInProgress}
          >
            <CIcon icon={cilTrash} />
          </CButton>
        </CTableDataCell>
      )}
    </CTableRow>
  );
}
